/* eslint-disable no-unused-vars */
import { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { generalActions } from 'store/campaign/general';
import { manageCampaignsAPI } from 'services/manageCampaigns.service';
import { campaignsManageAPI } from 'services/campaignsManage.service';
import { isAdminSelector } from 'store/selectors';
import { creativesActions, formErrorsCampaign_action } from 'store/actions';
import { isInvalidBid } from 'utils';
import { campaignEnvironment } from 'constants';
import {
  select_is_directBuy,
  select_current_campaign_obj,
  select_campaign_error_obj,
} from 'store/selectors';

export const useFacade2 = (environment) => {
  const dispatch = useDispatch();

  const isAdmin = useSelector(isAdminSelector);
  const minBidValueStore = useSelector((state) => state.general.minBidValue);
  const errorFlag = useSelector(select_campaign_error_obj).bidding;
  const currentCampaignObj = useSelector(select_current_campaign_obj);
  const isDirectBuy = useSelector(select_is_directBuy);
  const isWebsite = useSelector(
    (state) => state?.general?.mainCampaignObject?.currentCampaignObj?.isWebsite,
  );
  const isCpmBtn = !window.cpm_is_active ? false : isDirectBuy;
  const bidType = currentCampaignObj.bidType;
  const errorBorderFlag =
    (errorFlag && bidType === 0) ||
    (errorFlag && bidType == 1 && currentCampaignObj.isAllPlacements);

  const [expandedFlag, setExpandedFlag] = useState(false);

  // Get selected locations for countries count (UA Budget Validation)
  const selectedLocations = useSelector(
    (state) => state?.general?.mainCampaignObject?.selected_locations,
  );

  // UA Budget Validation (JIRA: UA Campaign Budget Validation):
  // - New / duplicate: full form snapshot, no campaignId.
  // - Edit campaign (full form): campaignId + full form snapshot from current state (not DB).
  // Backend may return rule = "LegacyMinimum" for legacy campaigns (no enforcement client-side).
  const { cmp_id } = useParams() || {};
  const isExistingCampaign = environment === campaignEnvironment.edit && Boolean(cmp_id);

  const [getMinBudget, { data: budgetValidationData }] =
    campaignsManageAPI.useGetMinimumRequiredBudgetMutation();
  const budgetValidationTimer = useRef(null);

  // Calculate number of selected placements from comma-separated list
  const placementsCount = useMemo(() => {
    const list = currentCampaignObj.target_apps_list;
    if (!list || list === '') return 0;
    return list.split(',').filter((x) => x).length;
  }, [currentCampaignObj.target_apps_list]);

  // Number of selected countries. "All Countries" mode (location === 1) sends null
  // so the backend can apply its own logic; otherwise send the actual count.
  const countriesCount = useMemo(() => {
    const isAllCountries = +currentCampaignObj.location === 1;
    if (isAllCountries) return null;
    if (!selectedLocations?.length) return null;
    return new Set(
      selectedLocations
        .filter((item) => item.status === 'include')
        .map((item) => item.country_code)
        .filter(Boolean),
    ).size;
  }, [selectedLocations, currentCampaignObj.location]);

  // Fetch minimum budget when relevant fields change (debounced 300ms)
  useEffect(() => {
    if (currentCampaignObj.bidType === 0) return;

    if (budgetValidationTimer.current) {
      clearTimeout(budgetValidationTimer.current);
    }

    const budgetValidationPayload = {
      bid: currentCampaignObj.bid || 0,
      bidType: currentCampaignObj.bidType,
      target_type: currentCampaignObj.target_type,
      placementsCount,
      numberOfCountries: countriesCount,
      ...(isExistingCampaign ? { campaignId: cmp_id } : {}),
    };

    budgetValidationTimer.current = setTimeout(() => {
      getMinBudget(budgetValidationPayload);
    }, 300);

    return () => {
      if (budgetValidationTimer.current) {
        clearTimeout(budgetValidationTimer.current);
      }
    };
  }, [
    isExistingCampaign,
    cmp_id,
    currentCampaignObj.bid,
    currentCampaignObj.bidType,
    currentCampaignObj.target_type,
    placementsCount,
    countriesCount,
  ]);

  // Budget validation result. applicableRule is one of:
  //   "CPI" | "Placements" | "Broad" | "None" | "LegacyMinimum"
  // For "LegacyMinimum" (backend's deterministic legacy-campaign signal) and "None",
  // we fall back to the original (pre-validation) error messages.
  const minimumBudget = budgetValidationData?.minimumBudget || 0;
  const applicableRule = budgetValidationData?.applicableRule || 'None';
  const isRuleActive =
    minimumBudget > 0 && applicableRule !== 'None' && applicableRule !== 'LegacyMinimum';
  const currentBidMinValue =
    currentCampaignObj.bidType === 1
      ? window.cpm_bid_minimum
      : isAdmin
      ? window.bid_minimum_admin
      : minBidValueStore;
  const currentBidMaxValue =
    currentCampaignObj.bidType === 1 ? window.cpm_bid_maximum : window.bid_maximum;
  const isBidValidForBudgetRule = !isInvalidBid(
    currentCampaignObj.bid,
    '0',
    currentBidMinValue,
    currentBidMaxValue,
  );
  const budgetRuleError =
    isRuleActive && isBidValidForBudgetRule && currentCampaignObj.budget < minimumBudget;

  const queryObject = useMemo(() => {
    return structuredClone(currentCampaignObj);
  }, [
    bidType,
    currentCampaignObj.app,
    currentCampaignObj.appTypes,
    currentCampaignObj.isAllPlacements,
    currentCampaignObj.userAgentsList,
    currentCampaignObj.userAgentsOSType,
    currentCampaignObj.userAgentsOSMinVer,
    currentCampaignObj.userAgentsOSList,
    currentCampaignObj.selectedLocationsList,
    currentCampaignObj.target_type,
    currentCampaignObj.target_apps_list,
    currentCampaignObj.userAgentsOSNameList,
    currentCampaignObj.customAudience_IsInclude,
    currentCampaignObj.acquireNewUserGoal,
    currentCampaignObj.campaignType,
    currentCampaignObj.location,
    currentCampaignObj.Tier,
  ]);

  const { data } = manageCampaignsAPI.useGetFactorBidQuery(queryObject);

  useEffect(() => {
    if (data?.minBidAlowed) {
      dispatch(
        generalActions.updateMinBidServerStates({
          minBid: data?.minBidAlowed,
          errorMsg: data?.errMsg,
        }),
      );
    }
  }, [data?.minBidAlowed, data?.errMsg]);

  useEffect(() => {
    if (+bidType !== 0) {
      setExpandedFlag(true);
    } else {
      setExpandedFlag(false);
    }
  }, [bidType]);

  const isCpm = currentCampaignObj.bidType === 1;
  const isCPI = currentCampaignObj.bidType === 3;
  const minBidValue = isCpm
    ? window.cpm_bid_minimum
    : isAdmin
    ? window.bid_minimum_admin
    : minBidValueStore;
  const maxBidValue = isCpm ? window.cpm_bid_maximum : window.bid_maximum;
  const maxDailyBudget = isCPI ? window.cpi_max_daily_budget : window.max_daily_budget;
  const minDailyBudget = isAdmin ? window.min_daily_budget_admin : window.min_daily_budget;

  useEffect(() => {
    // don't set an error if cpm/cpc/cpi is not chosen yet
    if (currentCampaignObj.bidType === 0) return;

    const isBidError = isInvalidBid(currentCampaignObj.bid, '0', minBidValue, maxBidValue);
    const isBudgetError = isInvalidBid(
      currentCampaignObj.budget,
      '0',
      minDailyBudget,
      maxDailyBudget,
    );

    dispatch(
      formErrorsCampaign_action.updateBidding(isBidError || isBudgetError || budgetRuleError),
    );
  }, [
    currentCampaignObj.bidType,
    currentCampaignObj.bid,
    currentCampaignObj.budget,
    minBidValue,
    maxBidValue,
    budgetRuleError,
  ]);

  const handleBidTypeChange = useCallback(
    (value) => {
      if (!expandedFlag) setExpandedFlag(true);
      if (value === bidType) return;

      dispatch(
        generalActions.updateCurrentCampaignObj({
          propField: 'bidType',
          newValue: value,
        }),
      );
      dispatch(generalActions.setMinBidAsNotRelevant());
      // dispatch(creativesActions.resetTrackingAndCreative());
    },
    [expandedFlag, bidType],
  );

  const showFlag = useMemo(() => {
    if (!currentCampaignObj.isOnBoarding && currentCampaignObj.reEngageGoal) return false;
    if (currentCampaignObj.isOnBoarding) return false;
    return true;
  }, [currentCampaignObj.isOnBoarding, currentCampaignObj.reEngageGoal]);

  return {
    handleBidTypeChange,
    // handleBudgetChange,
    expandedFlag,
    errorBorderFlag,
    bidType,
    isOnBoarding: currentCampaignObj.isOnBoarding,
    showFlag,
    budgetValue: currentCampaignObj.budget,
    acquireNewUserGoal: currentCampaignObj.acquireNewUserGoal,
    reEngageGoal: currentCampaignObj.reEngageGoal,
    isWebsite,
    isCpmBtn,
    minBidValue,
    maxBidValue,
    minDailyBudget,
    maxDailyBudget,
    isCpm,
    minBidValueStore,
    isAdmin,
    minimumBudget,
    applicableRule,
    budgetRuleError,
  };
};
